import { createStore, select, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { Injectable } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { config } from '../../environments/environment';
import { TAppColorMode } from '../shared/schemas/settings';
import { tenantStorageKey } from '../shared/helpers/tenant';

// TODO: remove
export let defaultSettingsTab:
  'video' | 'audio' | 'misc' | 'app' | 'permissions' | 'login' | 'developer' = 'audio';
export function setDefaultSettingsTab(
  value: 'video' | 'audio' | 'misc' | 'app' | 'permissions' | 'login' | 'developer'
) {
  defaultSettingsTab = value;
}

export type TMainSections =
  | 'about'
  | 'general-settings'
  | 'sign-in'
  | 'setup'
  | 'show-promo'
  | 'preparation-area'
  | 'room-login'
  // Landing view of a password-recovery link (`/#recover=<token>`); in-memory
  // only — a reload has no hash any more and lands on the pre-join screen.
  | 'recover-password';
export type TPanelSections = 'whiteboard' | 'chat' | 'video' | 'spatial';
export type TColorModes = TAppColorMode | null;
export type LoginAreaLogoSize = 'invisible' | 'small' | 'large';
export interface IAppState {
  activeSection: TMainSections;
  isConnectedView: boolean;
  loginAreaLogoSize: LoginAreaLogoSize;
  panelSettingsExpanded: boolean;
  appInfoExpanded: boolean;
}

const APP_STATE_STORE_NAME = 'andorAppState.v1';

const appStore = createStore(
  { name: APP_STATE_STORE_NAME },
  withProps<IAppState>({
    activeSection: 'room-login',
    isConnectedView: false,
    loginAreaLogoSize: 'small',
    panelSettingsExpanded: false,
    appInfoExpanded: false,
  })
);

if (config.persistAppState) {
  console.log('persistAppState', config.persistAppState);
  persistState(appStore, {
    key: tenantStorageKey(APP_STATE_STORE_NAME),
    storage: localStorageStrategy,
  });
}

@Injectable({
  providedIn: 'root',
})
export class AppStore {
  get store() {
    return appStore;
  }
  store$ = this.store.pipe(select((state) => state));
  get value(): IAppState {
    return this.store.getValue();
  }
  isConnectedView$ = this.store.pipe(select((state) => state.isConnectedView));
  activeSection$ = this.store.pipe(select((state) => state.activeSection));
  loginAreaLogoSize$: Observable<LoginAreaLogoSize> = combineLatest([
    this.store.pipe(select((state) => state.activeSection)),
    this.store.pipe(select((state) => state.loginAreaLogoSize)),
  ]).pipe(
    map(([section, size]) => (section === 'preparation-area' ? 'invisible' : size)),
    distinctUntilChanged()
  );
  panelSettingsExpanded$ = this.store.pipe(select((state) => state.panelSettingsExpanded));
  appInfoExpanded$ = this.store.pipe(select((state) => state.appInfoExpanded));

  update(app: Partial<IAppState>) {
    this.store.update((state) => ({
      ...state,
      ...app,
    }));
  }

  getConnectedClass$(): Observable<'connected' | ''> {
    return this.store$.pipe(
      select((state) => state.isConnectedView),
      map((state) => {
        if (state) {
          return 'connected';
        } else {
          return '';
        }
      })
    );
  }

  getPanelSettingsClass$(): Observable<'expanded' | ''> {
    return this.store$.pipe(
      select((state) => state.panelSettingsExpanded),
      map((state) => {
        if (state) {
          return 'expanded';
        } else {
          return '';
        }
      })
    );
  }

  togglePanelSettings() {
    this.store.update((state) => ({
      ...state,
      panelSettingsExpanded: !state.panelSettingsExpanded,
    }));
  }

  toggleConnected() {
    this.store.update((state) => ({
      ...state,
      isConnectedView: !state.isConnectedView,
    }));
  }
}
